import React, { useState, useEffect, useCallback, Suspense } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import API_URL from '../api';
import MessageStudentModal from '../components/MessageStudentModal';
import logoMark from '../assets/images/logo.jpg';
import { timeAgo } from '../utils/time';
import { mergeProfileIntoSession } from '../utils/session';
import { applicationRef } from '../applications/api';
import {
  AnimatedCounter,
  EmptyState,
  MetricTile,
  Modal,
  SkeletonList,
  TabNav,
} from '../ui';
import EntitlementCards from '../billing/EntitlementCards';
import PricingPlans from '../billing/PricingPlans';
import CheckoutModal from '../billing/CheckoutModal';
import { fetchEntitlements, fetchPackages } from '../billing/checkout';
import '../billing/billing.css';

// Lazy: the candidate AI drawer (and its panel) only loads when first opened.
const CandidateDetailDrawer = React.lazy(() => import('../components/CandidateDetailDrawer'));
// Lazy: the Recommended Students pool only loads when that tab is opened.
const TalentPool = React.lazy(() => import('../components/TalentPool'));

const SKILLS_FALLBACK = [
  'Python','JavaScript','TypeScript','React','Node.js','Java','C++','Go','Rust',
  'Machine Learning','Deep Learning','Data Analysis','TensorFlow','PyTorch',
  'AWS','Docker','Kubernetes','MongoDB','PostgreSQL','MySQL',
  'Flutter','Android','iOS','Next.js','Django','Flask','GraphQL',
  'Blockchain','Redis','Elasticsearch','C','Kotlin','Swift','R',
  'Vue.js','Angular','HTML/CSS','Redux','FastAPI','Spring Boot',
  'GCP','Azure','CI/CD','Linux','Pandas','NumPy','NLP','LLMs',
  'React Native','Solidity','REST APIs','Microservices','System Design',
  'Product Management','Product Strategy','User Research','Wireframing','Prototyping',
  'A/B Testing','Market Research','Business Analysis','Agile/Scrum','Roadmapping',
  'UI/UX Design','Figma','Adobe XD','Canva','Graphic Design','Motion Graphics',
  'Video Editing','Adobe Premiere Pro','After Effects','DaVinci Resolve','Final Cut Pro',
  'Content Writing','Copywriting','Script Writing','Podcast Production',
  'Digital Marketing','SEO','Social Media Marketing','Growth Hacking','Performance Marketing',
  'Financial Modeling','Excel/Sheets','Valuation','Investment Analysis','Consulting',
  'Project Management','Team Leadership','Event Management','Public Speaking',
  'Unity','Unreal Engine','Game Development','AR/VR',
];

const MAX_COMP = 50000;
const COMP_STEP = 1000;
const WORK_MODE_OPTIONS = ['Onsite', 'Hybrid', 'Remote'];

const STATUS_META = {
  draft:     { label: 'Draft',  color: '#6B7280', bg: '#F3F4F6' },
  published: { label: 'Live',   color: '#059669', bg: '#ECFDF5' },
  closed:    { label: 'Closed', color: '#DC2626', bg: '#FEE2E2' },
};

function daysAgo(dateStr) {
  if (!dateStr) return null;
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days}d ago`;
}

function formatComp(val) {
  if (!val && val !== 0) return '—';
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  return `₹${(val / 1000).toFixed(0)}k`;
}

// ── Dual-range compensation slider ─────────────────────────────────────────

function CompensationRange({ minVal, maxVal, onMinChange, onMaxChange }) {
  const minPct = (minVal / MAX_COMP) * 100;
  const maxPct = (maxVal / MAX_COMP) * 100;
  return (
    <div className="comp-range">
      <div className="comp-range-header">
        <span className="comp-range-display">
          {formatComp(minVal)} <span>to</span> {formatComp(maxVal)}
          <span style={{ marginLeft: 6 }}>/ month</span>
        </span>
      </div>
      <div className="comp-range-track-wrap">
        <div className="comp-range-track">
          <div className="comp-range-fill" style={{ left: `calc(${minPct}% + var(--thumb-inset))`, right: `calc(${100 - maxPct}% + var(--thumb-inset))` }} />
        </div>
        <input type="range" className="comp-range-input" min={0} max={MAX_COMP} step={COMP_STEP}
          value={minVal} onChange={e => onMinChange(Math.min(parseInt(e.target.value), maxVal - COMP_STEP))} />
        <input type="range" className="comp-range-input" min={0} max={MAX_COMP} step={COMP_STEP}
          value={maxVal} onChange={e => onMaxChange(Math.max(parseInt(e.target.value), minVal + COMP_STEP))} />
      </div>
      <div className="comp-range-bounds"><span>₹0</span><span>₹50k / month</span></div>
    </div>
  );
}

// ── Job row component ───────────────────────────────────────────────────────

function JobRow({ job, onStatusChange, onDelete, navigate, onViewApplicants }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const meta = STATUS_META[job.status] || STATUS_META.draft;
  const stipend = job.stipend?.min || job.stipend?.max
    ? `${job.stipend.currency || 'INR'} ${job.stipend.min?.toLocaleString('en-IN') ?? '?'} – ${job.stipend.max?.toLocaleString('en-IN') ?? '?'}`
    : null;
  const posted = new Date(job.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  const deadline = job.application_deadline
    ? new Date(job.application_deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    : null;

  return (
    <div className="jl-row">
      <div className="jl-row-main">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div className="jl-row-title">{job.title}</div>
          <span className="jl-status-badge" style={{ color: meta.color, background: meta.bg, flexShrink: 0 }}>
            {meta.label === 'Live' && <span className="ui-live-dot" style={{ marginRight: 6 }} />}
            {meta.label}
          </span>
        </div>
        <div className="jl-row-meta">
          <span>{job.position_type}</span>
          <span>·</span>
          <span>{job.work_mode}</span>
          {job.location && <><span>·</span><span>{job.location}</span></>}
          {stipend && <><span>·</span><span>{stipend}/mo</span></>}
          <span>·</span>
          <span>Posted {posted}</span>
          {job.num_openings > 1 && <><span>·</span><span>{job.num_openings} openings</span></>}
          {deadline && <><span>·</span><span style={{ color: '#C2410C' }}>Deadline {deadline}</span></>}
        </div>
        {job.skills?.length > 0 && (
          <div className="jl-row-skills" style={{ marginTop: 8 }}>
            {job.skills.slice(0, 6).map(s => <span key={s} className="rd-stag">{s}</span>)}
            {job.skills.length > 6 && <span className="rd-stag rd-stag--more">+{job.skills.length - 6}</span>}
          </div>
        )}
      </div>
      <div className="jl-row-right">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {/* The one action a recruiter opens this page to take. */}
          <button className="btn btn-sm jl-applicants-btn" onClick={() => onViewApplicants(job)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4 }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            View Applicants{job.applicant_count > 0 && <span className="jl-applicants-count">{job.applicant_count}</span>}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/recruiter/post-job/${job._id}`)}>Edit</button>
          {job.status === 'draft' && (
            <button className="btn btn-sm" style={{ background: '#059669' }} onClick={() => onStatusChange(job._id, 'published')}>Publish</button>
          )}
          {job.status === 'published' && (
            <button className="btn btn-secondary btn-sm" style={{ color: '#DC2626', borderColor: '#FECACA' }} onClick={() => onStatusChange(job._id, 'closed')}>Close</button>
          )}
          {job.status === 'closed' && (
            <button className="btn btn-secondary btn-sm" onClick={() => onStatusChange(job._id, 'published')}>Reopen</button>
          )}
          <div className="jl-menu-wrap">
            <button className="jl-menu-btn" onClick={() => setMenuOpen(v => !v)} aria-label="More actions">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
            </button>
            {menuOpen && (
              <div className="jl-menu" onMouseLeave={() => setMenuOpen(false)}>
                {job.status !== 'draft' && (
                  <button onClick={() => { onStatusChange(job._id, 'draft'); setMenuOpen(false); }}>Move to Draft</button>
                )}
                <button className="jl-menu-danger" onClick={() => { onDelete(job._id); setMenuOpen(false); }}>Delete Job</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Student card component ──────────────────────────────────────────────────

function StudentCard({ student, unlocked, onClick, onMessage }) {
  const college = student.college || student.iit_campus || '';
  const initial = college.replace(/^(IIT|NIT|MNNIT|MNIT|MANIT|SVNIT|VNIT)\s*/i, '').charAt(0) || 'S';
  const noticePeriod = student.notice_period || '';
  const workModes = Array.isArray(student.work_mode) ? student.work_mode : [];
  const hasRate = student.monthly_rate_min || student.monthly_rate_max;
  const rateLabel = hasRate
    ? `${student.monthly_rate_min ? formatComp(student.monthly_rate_min) : '?'} – ${student.monthly_rate_max ? formatComp(student.monthly_rate_max) : '?'}/mo`
    : null;

  return (
    <div className={`rd-card${unlocked ? ' rd-card--unlocked' : ''}`} onClick={onClick}>
      <div className="rd-card-top">
        <div className="rd-card-avatar">{initial}</div>
        <div className="rd-card-info">
          <div className="rd-card-college">{college}</div>
          <div className="rd-card-detail">Year {student.year} · {student.department}</div>
        </div>
        {unlocked
          ? <span className="rd-badge-unlocked">✓ Unlocked</span>
          : <span className="rd-badge-locked">🔒</span>}
      </div>
      <div className="rd-card-mid">
        {unlocked ? (
          <>
            <div className="rd-card-name">{student.name}</div>
            {student.bio && <div className="rd-card-bio">{student.bio}</div>}
          </>
        ) : (
          <>
            <div className="rd-mask rd-mask--name" />
            {student.bio && <div className="rd-mask rd-mask--bio" />}
          </>
        )}
      </div>
      {(noticePeriod || rateLabel || workModes.length > 0) && (
        <div className="rd-card-meta">
          {noticePeriod && (
            <span className="rd-avail-tag" title={student.notice_period_updated_at ? `Set ${daysAgo(student.notice_period_updated_at)}` : ''}>
              Joins: {noticePeriod}
              {student.notice_period_updated_at && (
                <span className="rd-stale-hint"> · {daysAgo(student.notice_period_updated_at)}</span>
              )}
            </span>
          )}
          {workModes.map(m => <span key={m} className="rd-avail-tag">{m}</span>)}
          {rateLabel && <span className="rd-rate-tag">{rateLabel}</span>}
        </div>
      )}
      <div className="rd-card-skills">
        {student.skills?.slice(0, 4).map(skill => (
          <span key={skill._id} className="rd-stag">{skill.name}</span>
        ))}
        {student.skills?.length > 4 && (
          <span className="rd-stag rd-stag--more">+{student.skills.length - 4}</span>
        )}
      </div>
      <div className="rd-card-foot">
        {unlocked
          ? <span className="rd-cta rd-cta--green">View Full Profile →</span>
          : <span className="rd-cta rd-cta--blue">Unlock Profile →</span>}
        {unlocked && (
          // stopPropagation: the whole card is a click target for the profile.
          <button
            className="rd-card-msg"
            onClick={(e) => { e.stopPropagation(); onMessage(student); }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            Message
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

function RecruiterDashboard({ onLogout }) {
  const [tab, setTab] = useState('jobs');
  const [availableSkills, setAvailableSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [useCompFilter, setUseCompFilter] = useState(false);
  const [compMin, setCompMin] = useState(0);
  const [compMax, setCompMax] = useState(50000);
  const [selectedWorkModes, setSelectedWorkModes] = useState([]);
  const [skillSearch, setSkillSearch] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [unlockedProfiles, setUnlockedProfiles] = useState(new Set());
  // Billing. Entitlements are always read from the server — never inferred from
  // what the UI thinks it just spent.
  const [entitlements, setEntitlements] = useState(null);
  const [entitlementsLoading, setEntitlementsLoading] = useState(true);
  const [packages, setPackages] = useState(null);
  const [buyModal, setBuyModal] = useState({ open: false, product: null });
  const [checkout, setCheckout] = useState({ open: false, pkg: null });
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [publishedJob, setPublishedJob] = useState(null);
  const [applicantsPanel, setApplicantsPanel] = useState({ open: false, jobId: null, jobTitle: '' });
  const [applicants, setApplicants] = useState([]);
  const [applicantsLoading, setApplicantsLoading] = useState(false);
  const [drawerTab, setDrawerTab] = useState('applicants');
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignForm, setAssignForm] = useState({ title: '', description: '', due_date: '' });
  const [assignSaving, setAssignSaving] = useState(false);
  const [submissionsPanel, setSubmissionsPanel] = useState({ open: false, assignment: null, list: [], loading: false });
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  // AI insights state (applicants panel)
  const [matchesByStudent, setMatchesByStudent] = useState({});
  const [aiSort, setAiSort] = useState('match');
  const [aiFilters, setAiFilters] = useState({ skills: '' });
  const [candidateDetail, setCandidateDetail] = useState({ open: false, student: null });
  const [messageTarget, setMessageTarget] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');

  const [recruiter, setRecruiter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  });
  const recruiterInitial = (recruiter.name || recruiter.company_name || 'R')[0].toUpperCase();
  const hasPhone = !!(recruiter.phone && recruiter.phone.trim());

  useEffect(() => {
    axios.get(`${API_URL}/api/recruiter/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        // Merged field-by-field, never spread wholesale: the recruiter
        // document's `role` is the person's job title, and letting it land on
        // the session snapshot's `role` would fail every ProtectedRoute check
        // from the next navigation onwards. See utils/session.js.
        setRecruiter(current => mergeProfileIntoSession(current, res.data));
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify(mergeProfileIntoSession(stored, res.data)));
      })
      .catch(() => { /* the local snapshot remains available as a fallback */ });
  }, [token]);

  const refreshEntitlements = useCallback(() => {
    setEntitlementsLoading(true);
    fetchEntitlements()
      .then(setEntitlements)
      .catch(() => { /* the cards render their empty state */ })
      .finally(() => setEntitlementsLoading(false));
  }, []);

  useEffect(() => { fetchSkills(); }, []);

  // Credits and the package catalog. Both are read from the server on every
  // mount so a purchase made in another tab (or a package that expired while
  // the tab sat open) is reflected rather than assumed.
  useEffect(() => { refreshEntitlements(); }, [refreshEntitlements]);

  useEffect(() => {
    fetchPackages()
      .then(setPackages)
      .catch(() => { /* the picker shows its skeleton and the CTA still routes to /pricing */ });
  }, []);

  useEffect(() => {
    fetchUnreadMsgCount();
    const interval = setInterval(fetchUnreadMsgCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (location.state?.jobPublished) {
      setTab('jobs');
      setPublishedJob({ title: location.state.jobPublished, id: location.state.jobId });
      fetchJobs();
      window.history.replaceState({}, '');
    }
  }, []);

  useEffect(() => {
    if (tab === 'jobs') fetchJobs();
  }, [tab]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/jobs`, { headers: { Authorization: `Bearer ${token}` } });
      setJobs(res.data);
    } catch { /* silent */ }
    finally { setJobsLoading(false); }
  }, [token]);

  const handleJobStatusChange = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/jobs/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      fetchJobs();
      if (status === 'published') showToast('Job published — students have been notified.');
    } catch { /* silent */ }
  };

  const handleJobDelete = async (id) => {
    if (!window.confirm('Delete this job? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/jobs/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchJobs();
    } catch { /* silent */ }
  };

  const handleViewApplicants = async (job) => {
    setApplicantsPanel({ open: true, jobId: job._id, jobTitle: job.title });
    setApplicants([]);
    setApplicantsLoading(true);
    setDrawerTab('applicants');
    setAssignments([]);
    setAssignForm({ title: '', description: '', due_date: '' });
    setSubmissionsPanel({ open: false, assignment: null, list: [], loading: false });
    setMatchesByStudent({});
    setAiFilters({ skills: '' });
    setCandidateDetail({ open: false, student: null });
    // Fetch applicants + AI matches together under one loading state so the
    // list renders once, fully populated — no chip flicker or reorder jump.
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [appsRes, matchesRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/jobs/${job._id}/applicants`, { headers }),
        axios.get(`${API_URL}/api/matches/job/${job._id}`, { headers }),
      ]);
      if (appsRes.status === 'fulfilled') setApplicants(appsRes.value.data);
      if (matchesRes.status === 'fulfilled') {
        setMatchesByStudent(Object.fromEntries(matchesRes.value.data.map(m => [String(m.student_id), m])));
      } // matches failure is non-fatal — list just shows "not evaluated"
    } finally { setApplicantsLoading(false); }
  };

  // Stable identity matters: JobMatchPanel keys its fetch effect off this —
  // an unstable callback would re-trigger fetches on every dashboard render.
  const handleMatchUpdated = useCallback((match) => {
    if (!match) return;
    setMatchesByStudent(prev => ({ ...prev, [String(match.student_id)]: match }));
  }, []);

  const fetchAssignments = async (jobId) => {
    setAssignmentsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/assignments/job/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAssignments(res.data);
    } catch { /* silent */ }
    finally { setAssignmentsLoading(false); }
  };

  const handleDrawerTabChange = (t) => {
    setDrawerTab(t);
    if (t === 'assignments' && assignments.length === 0 && !assignmentsLoading) {
      fetchAssignments(applicantsPanel.jobId);
    }
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!assignForm.title.trim() || !assignForm.description.trim()) return;
    setAssignSaving(true);
    try {
      await axios.post(`${API_URL}/api/assignments`, {
        job_id: applicantsPanel.jobId,
        title: assignForm.title,
        description: assignForm.description,
        due_date: assignForm.due_date || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAssignForm({ title: '', description: '', due_date: '' });
      fetchAssignments(applicantsPanel.jobId);
      showToast('Assignment created successfully.');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create assignment');
    } finally {
      setAssignSaving(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Delete this assignment and all submissions?')) return;
    try {
      await axios.delete(`${API_URL}/api/assignments/${assignmentId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchAssignments(applicantsPanel.jobId);
    } catch { /* silent */ }
  };

  const handleViewSubmissions = async (assignment) => {
    setSubmissionsPanel({ open: true, assignment, list: [], loading: true });
    try {
      const res = await axios.get(`${API_URL}/api/assignments/${assignment._id}/submissions`, { headers: { Authorization: `Bearer ${token}` } });
      setSubmissionsPanel(p => ({ ...p, list: res.data, loading: false }));
    } catch {
      setSubmissionsPanel(p => ({ ...p, loading: false }));
    }
  };

  const handleMarkReviewed = async (submissionId) => {
    try {
      await axios.put(`${API_URL}/api/assignments/submissions/${submissionId}/review`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSubmissionsPanel(p => ({
        ...p,
        list: p.list.map(s => s._id === submissionId ? { ...s, status: 'reviewed' } : s),
      }));
    } catch { /* silent */ }
  };

  const fetchUnreadMsgCount = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
      setUnreadMsgCount(res.data.count || 0);
    } catch { /* silent */ }
  };

  const fetchSkills = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/skills`);
      if (res.data && res.data.length > 0) {
        setAvailableSkills(res.data);
      } else {
        await axios.post(`${API_URL}/api/skills/seed`);
        const seeded = await axios.get(`${API_URL}/api/skills`);
        setAvailableSkills(seeded.data.length > 0 ? seeded.data : SKILLS_FALLBACK.map((name, i) => ({ _id: i, name })));
      }
    } catch {
      setAvailableSkills(SKILLS_FALLBACK.map((name, i) => ({ _id: i, name })));
    }
  };

  const toggleSkill = (skillName) => {
    setSelectedSkills(prev => {
      if (prev.includes(skillName)) return prev.filter(s => s !== skillName);
      if (prev.length >= 5) return prev;
      return [...prev, skillName];
    });
  };

  const handleSearch = async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams();
      selectedSkills.forEach(s => params.append('skills', s));
      if (useCompFilter) { params.append('rate_min', compMin); params.append('rate_max', compMax); }
      selectedWorkModes.forEach(m => params.append('work_modes', m));
      const res = await axios.get(`${API_URL}/api/students/search?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setStudents(res.data);

      // Lock state comes from the server, which is the only thing that knows
      // whether an unlock is still inside its validity window. Previously this
      // Set was filled by the client after a fake "I've paid" click, so a
      // refresh silently re-locked everything and an expired package did not
      // re-lock anything.
      const ids = (res.data || []).map(student => student._id);
      if (ids.length) {
        try {
          const unlockedRes = await axios.post(
            `${API_URL}/api/talent/unlocked`,
            { student_ids: ids },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setUnlockedProfiles(new Set(unlockedRes.data.student_ids || []));
        } catch {
          // Fall back to showing everything locked: opening a locked card costs
          // the recruiter nothing, whereas showing an unlock that is not real
          // would promise contact details the profile endpoint will withhold.
          setUnlockedProfiles(new Set());
        }
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally { setLoading(false); }
  };

  const handleCardClick = (student) => {
    if (unlockedProfiles.has(student._id)) {
      navigate(`/student/${student._id}`);
      return;
    }
    // Unlocking is done from the talent pool, where the server can attribute it
    // to a search. From here the useful action is topping up the credits that
    // make unlocking possible at all.
    setBuyModal({ open: true, product: 'profile' });
  };

  const handleMessageStudent = (student) => {
    setMessageTarget({
      id: student._id,
      name: student.name,
      subtitle: [student.college || student.iit_campus, student.department].filter(Boolean).join(' · '),
    });
  };

  // The previous "I've Completed Payment" button and its UPI QR are gone: they
  // unlocked a profile on the client's say-so, with no order, no verification
  // and no record that money had moved. Unlocking now happens server-side
  // against a real entitlement (POST /api/talent/unlock).


  const activeJobs   = jobs.filter(j => j.status === 'published').length;
  const draftJobs    = jobs.filter(j => j.status === 'draft').length;
  const closedJobs   = jobs.filter(j => j.status === 'closed').length;

  /* Hiring activity, built only from figures the API actually returns:
     applicant counts per job. There is no view tracking on the backend,
     so there is no views-over-time line to draw here - showing one would
     mean inventing it. */
  const totalApplicants = jobs.reduce((sum, j) => sum + (j.applicant_count || 0), 0);
  const topJobsByApplicants = [...jobs]
    .filter(j => (j.applicant_count || 0) > 0)
    .sort((a, b) => (b.applicant_count || 0) - (a.applicant_count || 0))
    .slice(0, 4);
  const busiestJobCount = topJobsByApplicants[0]?.applicant_count || 1;

  const JOB_STATUS_TILES = [
    {
      key: 'active', value: activeJobs, label: 'Active', hint: 'Live jobs', tone: 'success', live: true,
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
    },
    {
      key: 'drafts', value: draftJobs, label: 'Drafts', hint: 'Unpublished', tone: 'neutral',
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>,
    },
    {
      key: 'closed', value: closedJobs, label: 'Closed', hint: 'No longer accepting', tone: 'danger',
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><line x1="8" y1="12" x2="16" y2="12"/></svg>,
    },
    {
      key: 'applicants', value: totalApplicants, label: 'Applicants', hint: 'Across all roles', tone: 'primary',
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>,
    },
  ];

  const popularSkillsForSearch = [
    'Python','JavaScript','React','Node.js','Machine Learning','Data Analysis',
    'UI/UX Design','Figma','Product Management','Video Editing','Digital Marketing',
    'Java','C++','TypeScript','Next.js','Django','AWS','Docker','Flutter',
    'Financial Modeling','SEO','Graphic Design','Content Writing','Project Management','Canva',
  ];

  return (
    <div className="rd-page">

      {/* ── Toast notification ── */}
      {toast && (
        <div className="ui-toast" style={{ position: 'fixed', top: 20, right: 24, zIndex: 9999, background: '#ECFDF5', border: '1px solid #BBF7D0', borderRadius: 10, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: 'var(--ui-shadow-pop)', maxWidth: 360 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
          <span style={{ fontSize: 14, color: '#166534', fontWeight: 500 }}>{toast}</span>
        </div>
      )}

      {/* ── Phone missing banner ── */}
      {!hasPhone && (
        <div style={{ background: '#FFFBEB', borderBottom: '1px solid #FDE68A', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <span style={{ fontSize: 13, color: '#92400E', flex: 1 }}>
            <strong>Add your phone number</strong> to enable job posting.
          </span>
          <button onClick={() => navigate('/recruiter/profile')} style={{ fontSize: 12, fontWeight: 700, color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '5px 14px', cursor: 'pointer' }}>
            Complete Profile
          </button>
        </div>
      )}

      {/* ── Navbar ── */}
      <nav className="rd-nav" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div className="rd-nav-brand">
            <img src={logoMark} alt="" className="brand-mark" />
            <div>
              <div className="rd-nav-title">HYRSKILL</div>
              <div className="rd-nav-sub">Recruiter Portal</div>
            </div>
          </div>
          {(recruiter.company_name || recruiter.name) && (
            <div className="rd-nav-company-card" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: 12, flexShrink: 0 }}>
                {recruiterInitial}
              </div>
              <div>
                {recruiter.company_name && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>{recruiter.company_name}</div>}
                {recruiter.name && <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.2 }}>{recruiter.name}</div>}
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div title={!hasPhone ? 'Add your phone number in Profile to post jobs' : ''}>
            <button className="rd-post-job-btn" onClick={() => hasPhone ? navigate('/recruiter/post-job') : navigate('/recruiter/profile')} style={!hasPhone ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              <span className="rd-nav-btn-label">Post a Job</span>
            </button>
          </div>
          {/* Talent discovery is a primary action in its own right — a recruiter
              must never have to post a job just to search for students. */}
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/recruiter/find-students')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <span className="rd-nav-btn-label">Find Students</span>
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setUnreadMsgCount(0); navigate('/messages'); }} style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            <span className="rd-nav-btn-label">Messages</span>
            {/* Keyed on the count so a newly arrived message scales the badge
                in again rather than silently changing a digit. */}
            {unreadMsgCount > 0 && (
              <span key={unreadMsgCount} className="ui-badge-pop" style={{ position: 'absolute', top: -6, right: -6, background: '#EF4444', color: '#fff', borderRadius: '50%', fontSize: 10, fontWeight: 700, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1 }}>
                {unreadMsgCount > 99 ? '99+' : unreadMsgCount}
              </span>
            )}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/recruiter/profile')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <span className="rd-nav-btn-label">Profile</span>
          </button>
          <button className="rd-nav-logout" onClick={onLogout}><span className="rd-nav-btn-label">Sign Out</span><span className="rd-nav-btn-icon">✕</span></button>
        </div>
      </nav>

      {/* ── Tab bar (only real tabs — My Jobs & Find Candidates) ── */}
      {/* Find Candidates tab stays hidden — the strip is ready for it. */}
      <TabNav
        className="rd-tabs"
        tabClassName="rd-tab"
        ariaLabel="Recruiter sections"
        active={tab}
        onChange={setTab}
        items={[
          {
            id: 'jobs',
            label: 'My Jobs',
            badge: jobs.length,
            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
          },
        ]}
      />

      <div className="rd-body">

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* MY JOBS TAB                                                         */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {tab === 'jobs' && (
          <div className="jl-panel">

            {/* Header */}
            <div className="jl-header">
              <div>
                <h2 style={{ margin: 0 }}>My Jobs</h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                  Manage your job postings and track candidate interest.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" onClick={() => navigate('/recruiter/find-students')} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  Find Relevant Students
                </button>
                <div title={!hasPhone ? 'Add your phone number in Profile to post jobs' : ''}>
                  <button className="btn btn-primary" onClick={() => hasPhone ? navigate('/recruiter/post-job') : navigate('/recruiter/profile')} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, opacity: hasPhone ? 1 : 0.6, cursor: hasPhone ? 'pointer' : 'not-allowed' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Post a Job
                  </button>
                </div>
              </div>
            </div>

            {/* Loading — hold the shape of the list rather than a spinner */}
            {jobsLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div className="rd-status-tiles">
                  {[0, 1, 2, 3].map(i => <div key={i} className="ui-skel" style={{ height: 108, borderRadius: 12 }} />)}
                </div>
                <SkeletonList count={3} height={104} />
              </div>
            )}

            {/* Empty state */}
            {!jobsLoading && jobs.length === 0 && (
              <EmptyState
                art="rocket"
                title="Post your first opportunity"
                body="Reach verified students from IITs and NITs. Post a role and start receiving applications."
                action={() => hasPhone ? navigate('/recruiter/post-job') : navigate('/recruiter/profile')}
                actionLabel={hasPhone ? 'Post Your First Job' : 'Complete Profile to Post Jobs'}
                style={{ maxWidth: 520, margin: '0 auto', padding: '48px 24px' }}
                extra={
                  <ul className="rd-empty-benefits">
                    {[
                      'Reach verified IIT & NIT talent',
                      'Receive direct student applications',
                      'Filter candidates by skills & stipend',
                      'Track all applicants in one place',
                    ].map(benefit => (
                      <li key={benefit}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}

            {/* Post-publish success banner */}
            {publishedJob && (
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 14, padding: '18px 24px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#166534', marginBottom: 4 }}>
                    "{publishedJob.title}" is now live
                  </div>
                  <div style={{ fontSize: 13, color: '#15803D', marginBottom: 14 }}>
                    Students have been notified and can now apply.
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/* Find Matching Candidates hidden */}
                    {publishedJob.id && (
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/recruiter/post-job/${publishedJob.id}`)}>Edit Job</button>
                    )}
                    <button style={{ background: 'none', border: 'none', fontSize: 13, color: '#15803D', cursor: 'pointer', padding: '5px 0', fontWeight: 600 }} onClick={() => setPublishedJob(null)}>Dismiss</button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Credits ──
                Above the job list because they gate it: a recruiter with no job
                credits needs to know that before clicking Post a Job, and one
                whose profile access has lapsed needs to know before wondering
                why a candidate's details vanished. */}
            <div style={{ marginBottom: 22 }}>
              <EntitlementCards
                entitlements={entitlements}
                loading={entitlementsLoading}
                onBuy={(product) => setBuyModal({ open: true, product })}
                onFindStudents={() => navigate('/recruiter/find-students')}
                onPostJob={() => (hasPhone ? navigate('/recruiter/post-job') : navigate('/recruiter/profile'))}
              />
              <div style={{ marginTop: 10, textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={() => navigate('/recruiter/billing')}
                  style={{ background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 600, color: 'var(--primary)', cursor: 'pointer' }}
                >
                  Billing &amp; payment history →
                </button>
              </div>
            </div>

            {/* Status at a glance, plus where the interest is landing */}
            {!jobsLoading && jobs.length > 0 && (
              <div className="rd-overview">
                <div className="rd-status-tiles">
                  {JOB_STATUS_TILES.map(t => (
                    <MetricTile
                      key={t.key}
                      value={t.value}
                      label={t.label}
                      hint={t.hint}
                      tone={t.tone}
                      icon={t.icon}
                      live={t.live}
                    />
                  ))}
                </div>

                {/* Quick insight, not an analytics product: which roles are
                    pulling applicants, sized against the busiest one. */}
                <div className="rd-insight">
                  <div className="rd-insight__head">
                    <div>
                      <div className="rd-insight__label">Applicant interest</div>
                      <div className="rd-insight__value">
                        <AnimatedCounter value={totalApplicants} />
                        <span> total</span>
                      </div>
                    </div>
                    <span className="rd-insight__scope">
                      {activeJobs} live role{activeJobs === 1 ? '' : 's'}
                    </span>
                  </div>

                  {topJobsByApplicants.length === 0 ? (
                    <p className="rd-insight__empty">
                      No applications yet. They appear here the moment students start applying.
                    </p>
                  ) : (
                    <ul className="rd-insight__list">
                      {topJobsByApplicants.map(job => (
                        <li key={job._id}>
                          <button type="button" onClick={() => handleViewApplicants(job)}>
                            <span className="rd-insight__job">{job.title}</span>
                            <span className="rd-insight__count">{job.applicant_count}</span>
                          </button>
                          <span className="rd-insight__bar" aria-hidden="true">
                            <i style={{ width: `${Math.round((job.applicant_count / busiestJobCount) * 100)}%` }} />
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {/* Job list grouped by status */}
            {!jobsLoading && jobs.length > 0 && (
              <div className="jl-list">
                {['published', 'draft', 'closed'].map(status => {
                  const group = jobs.filter(j => j.status === status);
                  if (!group.length) return null;
                  const m = STATUS_META[status];
                  return (
                    <div key={status} className="jl-group">
                      <div className="jl-group-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, display: 'inline-block' }} />
                        {m.label} ({group.length})
                      </div>
                      {group.map(job => (
                        <JobRow key={job._id} job={job} onStatusChange={handleJobStatusChange} onDelete={handleJobDelete} navigate={navigate} onViewApplicants={handleViewApplicants} />
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* FIND CANDIDATES TAB                                                 */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {tab === 'search' && (
          <>
            {/* No active jobs nudge */}
            {jobs.length === 0 && (
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>💡</span>
                <div style={{ flex: 1, fontSize: 14, color: '#92400E' }}>
                  <strong>Post a job first</strong> to receive candidate recommendations matched to your role requirements.
                </div>
                <button className="btn btn-sm" style={{ flexShrink: 0 }} onClick={() => navigate('/recruiter/post-job')}>Post a Job</button>
              </div>
            )}

            {/* Search panel */}
            <div className="rd-search-panel">
              <div className="rd-search-heading">
                <h2>Discover IIT &amp; NIT Talent</h2>
                <p>Filter verified candidates by skills, compensation, and work preferences</p>
              </div>

              {/* Skills filter */}
              <div className="rd-filter-block">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span className="rd-label" style={{ marginBottom: 0 }}>
                    Skills
                    <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12, marginLeft: 6 }}>— up to 5</span>
                  </span>
                  {selectedSkills.length > 0 && (
                    <button className="rd-clear" onClick={() => setSelectedSkills([])}>Clear all</button>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Search skills..."
                  value={skillSearch}
                  onChange={e => setSkillSearch(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, marginBottom: 10, background: 'var(--bg)', color: 'var(--text)', outline: 'none', boxSizing: 'border-box' }}
                />
                <div className="rd-chips">
                  {(skillSearch.trim()
                    ? availableSkills.filter(s => s.name.toLowerCase().includes(skillSearch.toLowerCase()))
                    : availableSkills.filter(s => popularSkillsForSearch.includes(s.name))
                  ).map(skill => {
                    const isSelected = selectedSkills.includes(skill.name);
                    const isDisabled = !isSelected && selectedSkills.length >= 5;
                    return (
                      <button key={skill._id}
                        className={`rd-chip${isSelected ? ' rd-chip--on' : ''}${isDisabled ? ' rd-chip--disabled' : ''}`}
                        onClick={() => toggleSkill(skill.name)} disabled={isDisabled}>
                        {isSelected && <span className="rd-chip-tick">✓</span>}
                        {skill.name}
                      </button>
                    );
                  })}
                </div>
                <div className="rd-chip-meta">
                  <span>{selectedSkills.length}/5 selected</span>
                </div>
              </div>

              {/* Stipend budget filter */}
              <div className="rd-filter-block">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span className="rd-label" style={{ marginBottom: 0 }}>Stipend Budget</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={useCompFilter} onChange={e => setUseCompFilter(e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: 'var(--primary)', cursor: 'pointer' }} />
                    Enable filter
                  </label>
                </div>
                <div style={{ opacity: useCompFilter ? 1 : 0.35, pointerEvents: useCompFilter ? 'all' : 'none', transition: 'opacity 0.2s' }}>
                  <CompensationRange minVal={compMin} maxVal={compMax} onMinChange={setCompMin} onMaxChange={setCompMax} />
                </div>
              </div>

              {/* Work mode filter */}
              <div className="rd-filter-block">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span className="rd-label" style={{ marginBottom: 0 }}>Work Mode</span>
                  {selectedWorkModes.length > 0 && (
                    <button className="rd-clear" onClick={() => setSelectedWorkModes([])}>Clear</button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {WORK_MODE_OPTIONS.map(mode => {
                    const on = selectedWorkModes.includes(mode);
                    return (
                      <button key={mode}
                        className={`rd-chip${on ? ' rd-chip--on' : ''}`}
                        onClick={() => setSelectedWorkModes(prev => on ? prev.filter(m => m !== mode) : [...prev, mode])}>
                        {on && <span className="rd-chip-tick">✓</span>}
                        {mode}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Search action */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
                <button className="rd-search-btn" onClick={handleSearch} disabled={loading}>
                  {loading
                    ? <><span className="rd-spin" /> Searching…</>
                    : <>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        Search Candidates
                      </>}
                </button>
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div className="rd-state-center">
                <div className="rd-big-spin" />
                <p>Finding top talent…</p>
              </div>
            )}

            {/* Pre-search prompt */}
            {!loading && !hasSearched && (
              <div className="rd-state-center">
                <div className="rd-state-icon">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </div>
                <h3>Find Your Next Hire</h3>
                <p>Select skills above and click Search to discover verified candidates</p>
              </div>
            )}

            {/* Results */}
            {!loading && hasSearched && (
              <div className="rd-results">
                <div className="rd-results-meta">
                  <h3>{students.length} candidate{students.length !== 1 ? 's' : ''} found</h3>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {selectedSkills.map(s => <span key={s} className="rd-filter-pill">{s}</span>)}
                    {useCompFilter && (
                      <span className="rd-filter-pill" style={{ background: 'var(--accent-light)', color: 'var(--accent-dark)' }}>
                        {formatComp(compMin)} – {formatComp(compMax)}/mo
                      </span>
                    )}
                    {selectedWorkModes.map(m => (
                      <span key={m} className="rd-filter-pill" style={{ background: '#F0FDF4', color: '#15803D' }}>{m}</span>
                    ))}
                  </div>
                </div>

                {students.length === 0 ? (
                  <div className="rd-state-center">
                    <div className="rd-state-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    </div>
                    <h4>No candidates found</h4>
                    <p>Try adjusting your skill filters or expanding the compensation range</p>
                  </div>
                ) : (
                  <div className="rd-grid">
                    {students.map(student => (
                      <StudentCard key={student._id} student={student} unlocked={unlockedProfiles.has(student._id)} onClick={() => handleCardClick(student)} onMessage={handleMessageStudent} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Package picker ──
          Replaces the old UPI-QR paywall. Prices, credits and validity are
          rendered from the server catalog; selecting one opens Razorpay. */}
      <Modal
        open={buyModal.open}
        onClose={() => setBuyModal({ open: false, product: null })}
        maxWidth={900}
        labelledBy="rd-buy-title"
      >
        <div style={{ padding: '28px 26px 24px' }}>
          <h2 id="rd-buy-title" style={{ fontSize: 20, marginBottom: 6 }}>
            {buyModal.product === 'job' ? 'Buy job posts' : 'Unlock student profiles'}
          </h2>
          <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 22 }}>
            {buyModal.product === 'job'
              ? 'Each credit publishes one job, live immediately for 30 days.'
              : 'Each credit unlocks one student’s contact details and resume, for as long as your package is valid.'}
          </p>
          {packages ? (
            <PricingPlans
              packages={packages}
              onSelect={(pkg) => { setBuyModal({ open: false, product: null }); setCheckout({ open: true, pkg }); }}
              disabled={packages.payments_enabled === false}
              products={buyModal.product ? [buyModal.product] : ['profile', 'job']}
              compact
            />
          ) : (
            <div className="bl-plans">
              {[0, 1, 2].map(i => <div key={i} className="ui-skel" style={{ height: 300, borderRadius: 16 }} />)}
            </div>
          )}
        </div>
      </Modal>

      <CheckoutModal
        open={checkout.open}
        pkg={checkout.pkg}
        onClose={() => { setCheckout({ open: false, pkg: null }); refreshEntitlements(); }}
        onSuccess={refreshEntitlements}
      />

      {/* ── Applicants / Assignments panel ── */}
      {applicantsPanel.open && (
        <>
          <div onClick={() => setApplicantsPanel({ open: false })} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1100 }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 520, maxWidth: '100vw', background: '#fff', boxShadow: '-4px 0 40px rgba(0,0,0,0.18)', zIndex: 1101, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Drawer header */}
            <div style={{ padding: '18px 24px 0', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--text-primary)', marginBottom: 2 }}>{applicantsPanel.jobTitle}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{applicants.length} applicant{applicants.length !== 1 ? 's' : ''}</div>
                </div>
                <button onClick={() => setApplicantsPanel({ open: false })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, borderRadius: 6 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              {/* Tab switcher */}
              <div style={{ display: 'flex', gap: 0, borderBottom: 'none' }}>
                {['applicants', 'recommended', 'assignments'].map(t => (
                  <button key={t} onClick={() => handleDrawerTabChange(t)}
                    style={{ padding: '8px 18px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer', borderBottom: drawerTab === t ? '2px solid var(--primary)' : '2px solid transparent', color: drawerTab === t ? 'var(--primary)' : 'var(--text-muted)', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                    {t === 'applicants' ? 'Applicants' : t === 'recommended' ? 'Recommended Students' : 'Assignments'}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Applicants tab ── */}
            {drawerTab === 'applicants' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
                {applicantsLoading && (
                  <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
                    <div className="rd-big-spin" style={{ margin: '0 auto 12px' }} />
                    <p style={{ fontSize: 14 }}>Loading applicants…</p>
                  </div>
                )}
                {!applicantsLoading && applicants.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--text-muted)' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: 14, opacity: 0.4 }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>No applications yet</p>
                    <p style={{ fontSize: 13 }}>Students will appear here once they apply.</p>
                  </div>
                )}
                {!applicantsLoading && applicants.length > 0 && (() => {
                  // Merge AI data onto applicants, then filter + sort client-side.
                  const rows = applicants
                    .filter(app => app.student_id)
                    .map(app => ({
                      app,
                      s: app.student_id,
                      match: matchesByStudent[String(app.student_id._id)] || null,
                      resumeScore: app.evaluation_summary?.total ?? null,
                    }));
                  // Scores order the list but are never surfaced: no numeric
                  // threshold filters, no recommendation filter, no visible
                  // values. Skills are the only recruiter-facing criterion.
                  const skillTerms = aiFilters.skills.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
                  const filtered = rows.filter(({ s, match }) => {
                    if (skillTerms.length) {
                      const have = [];
                      (match?.skill_coverage || []).forEach(c => { if (c.status !== 'missing') have.push(String(c.skill).toLowerCase()); });
                      (s.skills || []).forEach(sk => have.push(String(sk.name || sk).toLowerCase()));
                      if (!skillTerms.every(t => have.some(h => h.includes(t)))) return false;
                    }
                    return true;
                  });
                  const sorted = [...filtered].sort((a, b) => {
                    if (aiSort === 'match') return (b.match?.match_score ?? -1) - (a.match?.match_score ?? -1);
                    if (aiSort === 'resume') return (b.resumeScore ?? -1) - (a.resumeScore ?? -1);
                    if (aiSort === 'evaluated') return new Date(b.app.evaluation_summary?.created_at || 0) - new Date(a.app.evaluation_summary?.created_at || 0);
                    return new Date(b.app.applied_at) - new Date(a.app.applied_at);
                  });
                  const inputStyle = { width: 74, fontSize: 12, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg)', color: 'var(--text-primary)' };
                  return (
                    <>
                      {/* ── AI sort + filter bar ── */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 14, padding: '10px 12px', background: '#F8FAFC', border: '1px solid var(--border)', borderRadius: 10 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Sort</span>
                        {/* Ordering only — the labels describe the ordering, not
                            a score the recruiter can read off. */}
                        <select value={aiSort} onChange={e => setAiSort(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
                          <option value="match">Best match for this job</option>
                          <option value="resume">Strongest profile</option>
                          <option value="evaluated">Latest evaluation</option>
                          <option value="applied">Applied date</option>
                        </select>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginLeft: 6 }}>Skills</span>
                        <input type="text" placeholder="e.g. React, Django" value={aiFilters.skills} onChange={e => setAiFilters(f => ({ ...f, skills: e.target.value }))} style={{ ...inputStyle, width: 150 }} />
                        {aiFilters.skills && (
                          <button onClick={() => setAiFilters({ skills: '' })} style={{ fontSize: 11, fontWeight: 600, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer' }}>Clear</button>
                        )}
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{sorted.length}/{rows.length} shown</span>
                      </div>
                      {sorted.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--text-muted)', fontSize: 13 }}>
                          No applicants match the current filters.
                        </div>
                      )}
                      {sorted.map(({ app, s, match, resumeScore }) => {
                  const college = s.college || s.iit_campus || '';
                  const initial = (s.name || college || 'S')[0].toUpperCase();
                  const statusColors = {
                    'Applied':             { color: '#1D4ED8', bg: '#DBEAFE' },
                    'Viewed':              { color: '#6B7280', bg: '#F3F4F6' },
                    'Shortlisted':         { color: '#059669', bg: '#D1FAE5' },
                    'Interview Scheduled': { color: '#7C3AED', bg: '#EDE9FE' },
                    'Selected':            { color: '#065F46', bg: '#A7F3D0' },
                    'Rejected':            { color: '#DC2626', bg: '#FEE2E2' },
                  };
                  const sm = statusColors[app.status] || statusColors['Applied'];
                  const appliedDate = new Date(app.applied_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                  return (
                    <div key={app._id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px', marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: 15, flexShrink: 0 }}>
                          {initial}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <button onClick={() => navigate(`/student/${s._id}`)} style={{ fontWeight: 700, fontSize: 15, color: 'var(--primary)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginBottom: 2, textAlign: 'left' }}>{s.name || 'Unknown'}</button>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            {[college, s.department, s.year ? `Year ${s.year}` : null].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: sm.color, background: sm.bg, padding: '3px 10px', borderRadius: 20, flexShrink: 0 }}>{app.status}</span>
                      </div>
                      {/* ── AI insight strip ──
                          Qualitative only: which of the job's skills this
                          candidate demonstrably has. No score, percentage or
                          recommendation badge — see the no-scores product rule. */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 10, padding: '7px 10px', background: '#F8FAFC', border: '1px solid var(--border)', borderRadius: 9 }}>
                        {match?.status === 'completed' && (match.skill_coverage || []).filter(c => c.status === 'matched').slice(0, 4).map(c => (
                          <span key={c.skill} style={{ fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#DCFCE7', padding: '2px 8px', borderRadius: 20 }}>✓ {c.skill}</span>
                        ))}
                        {match?.status === 'completed' && (match.missing_skills || []).slice(0, 2).map(skill => (
                          <span key={skill} style={{ fontSize: 11, fontWeight: 600, color: '#B45309', background: '#FEF3C7', padding: '2px 8px', borderRadius: 20 }}>Gap: {skill}</span>
                        ))}
                        {match?.status === 'processing' && <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>⏳ analyzing…</span>}
                        {resumeScore != null && (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: '#EEF2FF', color: '#4338CA' }}>
                            Resume analyzed
                          </span>
                        )}
                        {app.evaluation_summary?.created_at && (
                          <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>evaluated {timeAgo(app.evaluation_summary.created_at)}</span>
                        )}
                        {!match && resumeScore == null && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Not evaluated yet</span>}
                      </div>
                      {s.bio && <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{s.bio.length > 140 ? s.bio.slice(0, 137) + '…' : s.bio}</p>}
                      {s.skills?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                          {s.skills.slice(0, 5).map(sk => (
                            <span key={sk._id || sk.name || sk} style={{ fontSize: 11, fontWeight: 600, background: '#EEF2FF', color: '#4338CA', padding: '2px 9px', borderRadius: 20 }}>
                              {sk.name || sk}
                            </span>
                          ))}
                          {s.skills.length > 5 && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>+{s.skills.length - 5} more</span>}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Applied {appliedDate}</span>
                        {/* The same reference the candidate is shown on their
                            own application, so a follow-up quoting it can be
                            matched to a row here. */}
                        {applicationRef(app) && (
                          <span
                            title="Application ID"
                            style={{
                              fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              color: 'var(--text-muted)', background: 'var(--surface)',
                              border: '1px solid var(--border)', borderRadius: 6, padding: '1px 7px',
                            }}
                          >
                            {applicationRef(app)}
                          </span>
                        )}
                        {s.resume_url && <a href={s.resume_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }}>Resume</a>}
                        {s.linkedin_url && <a href={s.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: '#0A66C2', textDecoration: 'none' }}>LinkedIn</a>}
                        {s.github_url && <a href={s.github_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: '#111827', textDecoration: 'none' }}>GitHub</a>}
                        {s.portfolio_url && <a href={s.portfolio_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: '#059669', textDecoration: 'none' }}>Portfolio</a>}
                        <button
                          onClick={() => setCandidateDetail({ open: true, student: s })}
                          style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#7C3AED', border: 'none', borderRadius: 7, padding: '3px 12px', cursor: 'pointer' }}>
                          ✦ AI Insights
                        </button>
                        <button
                          onClick={() => navigate(`/student/${s._id}`)}
                          style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', background: 'var(--primary-light)', border: 'none', borderRadius: 7, padding: '3px 12px', cursor: 'pointer' }}>
                          View Profile
                        </button>
                        <button
                          onClick={() => handleMessageStudent(s)}
                          style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: 'var(--primary)', border: 'none', borderRadius: 7, padding: '3px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                          Message
                        </button>
                      </div>
                    </div>
                  );
                      })}
                    </>
                  );
                })()}
                {candidateDetail.open && (
                  <Suspense fallback={null}>
                    <CandidateDetailDrawer
                      open={candidateDetail.open}
                      onClose={() => setCandidateDetail({ open: false, student: null })}
                      student={candidateDetail.student}
                      jobId={applicantsPanel.jobId}
                      jobTitle={applicantsPanel.jobTitle}
                      onMatchUpdated={handleMatchUpdated}
                    />
                  </Suspense>
                )}
              </div>
            )}

            {/* ── Recommended Students tab ── */}
            {/* Populated automatically when the job was published, so this is
                usually already computed by the time a recruiter opens it. */}
            {drawerTab === 'recommended' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
                <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>}>
                  <TalentPool
                    jobId={applicantsPanel.jobId}
                    embedded
                    onViewProfile={(candidate) => navigate(`/student/${candidate.student_id}`)}
                  />
                </Suspense>
              </div>
            )}

            {/* ── Assignments tab ── */}
            {drawerTab === 'assignments' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>

                {/* Create assignment form */}
                <div style={{ background: '#F8FAFC', border: '1px solid var(--border)', borderRadius: 14, padding: 18, marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 14 }}>Create Assignment</div>
                  <form onSubmit={handleCreateAssignment}>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5 }}>Title <span style={{ color: '#DC2626' }}>*</span></label>
                      <input type="text" value={assignForm.title} onChange={e => setAssignForm(f => ({ ...f, title: e.target.value }))}
                        placeholder="e.g. Design a landing page mockup"
                        maxLength={200}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 9, border: '1.5px solid var(--border)', fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' }}
                        required />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5 }}>Instructions <span style={{ color: '#DC2626' }}>*</span></label>
                      <textarea value={assignForm.description} onChange={e => setAssignForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Describe what candidates should do, deliverables, evaluation criteria…"
                        rows={4} maxLength={2000} required
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 9, border: '1.5px solid var(--border)', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' }} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5 }}>Due Date (optional)</label>
                      <input type="date" value={assignForm.due_date} onChange={e => setAssignForm(f => ({ ...f, due_date: e.target.value }))}
                        style={{ padding: '8px 12px', borderRadius: 9, border: '1.5px solid var(--border)', fontSize: 13, outline: 'none', background: '#fff' }} />
                    </div>
                    <button type="submit" disabled={assignSaving || !assignForm.title.trim() || !assignForm.description.trim()}
                      style={{ background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 22px', fontSize: 13, fontWeight: 700, cursor: assignSaving ? 'not-allowed' : 'pointer', opacity: assignSaving ? 0.7 : 1 }}>
                      {assignSaving ? 'Creating…' : 'Create Assignment'}
                    </button>
                  </form>
                </div>

                {/* Existing assignments */}
                {assignmentsLoading && (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                    <div className="rd-big-spin" style={{ margin: '0 auto 10px' }} />
                    <p style={{ fontSize: 13 }}>Loading assignments…</p>
                  </div>
                )}
                {!assignmentsLoading && assignments.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-muted)' }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: 10, opacity: 0.4 }}><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
                    <p style={{ fontWeight: 600, fontSize: 13, margin: 0 }}>No assignments yet</p>
                    <p style={{ fontSize: 12, marginTop: 4 }}>Create one above to assess candidates.</p>
                  </div>
                )}
                {!assignmentsLoading && assignments.map(a => {
                  const dueDate = a.due_date ? new Date(a.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
                  const isOverdue = a.due_date && new Date(a.due_date) < new Date();
                  return (
                    <div key={a._id} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', flex: 1 }}>{a.title}</div>
                        <button onClick={() => handleDeleteAssignment(a._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: '2px 4px', flexShrink: 0, fontSize: 11, fontWeight: 600, opacity: 0.7 }}>Delete</button>
                      </div>
                      <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        {a.description.length > 120 ? a.description.slice(0, 117) + '…' : a.description}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        {dueDate && (
                          <span style={{ fontSize: 11, color: isOverdue ? '#DC2626' : 'var(--text-muted)', fontWeight: isOverdue ? 700 : 400 }}>
                            {isOverdue ? 'Overdue: ' : 'Due: '}{dueDate}
                          </span>
                        )}
                        <button onClick={() => handleViewSubmissions(a)}
                          style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', background: 'var(--primary-light, #EEF2FF)', border: '1px solid #C7D2FE', borderRadius: 8, padding: '5px 14px', cursor: 'pointer' }}>
                          View Submissions
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Submissions sub-panel */}
                {submissionsPanel.open && (
                  <div style={{ background: '#fff', border: '1.5px solid var(--primary)', borderRadius: 14, padding: 16, marginTop: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                        Submissions — {submissionsPanel.assignment?.title}
                      </div>
                      <button onClick={() => setSubmissionsPanel({ open: false, assignment: null, list: [], loading: false })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1 }}>×</button>
                    </div>
                    {submissionsPanel.loading && <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>Loading…</p>}
                    {!submissionsPanel.loading && submissionsPanel.list.length === 0 && (
                      <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>No submissions yet.</p>
                    )}
                    {!submissionsPanel.loading && submissionsPanel.list.map(sub => {
                      const st = sub.student_id;
                      const submittedAt = new Date(sub.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                      return (
                        <div key={sub._id} style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{st?.name || 'Student'}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{st?.college} · Submitted {submittedAt}</div>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: sub.status === 'reviewed' ? '#D1FAE5' : '#DBEAFE', color: sub.status === 'reviewed' ? '#065F46' : '#1D4ED8' }}>
                              {sub.status === 'reviewed' ? 'Reviewed' : 'Submitted'}
                            </span>
                          </div>
                          {sub.comments && <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--text-secondary)' }}>{sub.comments}</p>}
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            {sub.file_url && <a href={`${API_URL}${sub.file_url}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)' }}>Download File</a>}
                            {sub.link_url && <a href={sub.link_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: '#059669' }}>Open Link</a>}
                            {sub.status !== 'reviewed' && (
                              <button onClick={() => handleMarkReviewed(sub._id)} style={{ fontSize: 12, fontWeight: 600, color: '#065F46', background: '#ECFDF5', border: '1px solid #BBF7D0', borderRadius: 7, padding: '3px 12px', cursor: 'pointer', marginLeft: 'auto' }}>
                                Mark Reviewed
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <MessageStudentModal
        open={!!messageTarget}
        student={messageTarget}
        onClose={() => setMessageTarget(null)}
        onSent={(s) => showToast(`Message sent to ${s.name}.`)}
      />
    </div>
  );
}

export default RecruiterDashboard;
